import { useTranslation } from "react-i18next"
import { Card } from "../Card"
import { ProjectSettingGroup } from "./ProjectSettingGroup"
import { BoundaryInputType, Point } from "../../types"
import { useState, useEffect } from "react"
import { useQuery } from "@apollo/client"
import { GET_GEOCOORDINATES, GET_PROJECTEDCOORDINATES } from "../../api/QuadriApi"
import {
  convertJSONToWKT,
  convertWktToJSON,
  dropInteriorRings,
  reverseCoordinates,
  roundCoordinates
} from "../../util/projectSettings"
import { EditProjectBoundary, WarningMessage } from "./EditProjectBoundary"
import EditProjectBoundaryModal from "./EditProjectBoundaryModal"
import { projectOriginState } from "../StateSelectors"
import ConnectApi from "../../api/ConnectApi"
import { useRecoilValue } from "recoil"
import { CRSType } from "../../types"
import { areasMatch, crsNamesAreEqual } from "../../util/projectSettings"

// The card to render Connect project settings
const ImportedProjectSettings = (props: {
  connectProjectId: string
  unitSystem?: string
  boundaryCoordinates?: string
  crsName?: string
  tcpsArea?: Point[]
  tcpsCrsName?: string
  projectEnabled?: boolean
  csib64?: string
  submitQuadriProject: (e?: any) => void
  disableEdit?: boolean
}) => {
  const {
    connectProjectId,
    boundaryCoordinates,
    crsName,
    tcpsArea,
    tcpsCrsName,
    projectEnabled,
    csib64,
    submitQuadriProject,
    disableEdit
  } = props
  const { t } = useTranslation("config")
  const [ProjectedCoordinates, setProjectedCoordinates] = useState<string>()
  const [updatedBoundaryCoordinates, setUpdatedBoundaryCoordinates] = useState<string>()
  const [showProjectBoundaryModal, setShowProjectBoundaryModal] = useState(false)
  const [boundaryEdited, setBoundaryEdited] = useState(false)
  const [formData, setFormData] = useState<string>()
  const [initialFormData, setInitialFormData] = useState<string>()
  const [formDatatobeSent, setFormDatatoBeSent] = useState<string>()
  const projectOrigin = useRecoilValue(projectOriginState)
  const [connectApi, setConnectApi] = useState<ConnectApi | undefined>(undefined)
  const [active, setActive] = useState<CRSType>(CRSType.GeoCRS)
  const [boundaryInput, setBoundaryInput] = useState<BoundaryInputType>(BoundaryInputType.json)
  const [loading, setLoading] = useState<boolean>(false)

  const {
    data: projectedCoordinatesData,
    error: projectedCoordinatesError,
    loading: projectedCoordinatesIsLoading
  } = useQuery(GET_PROJECTEDCOORDINATES, {
    variables: {
      csib64,
      boundaryCoordinates: reverseCoordinates(boundaryCoordinates ?? "")
    },
    skip: csib64 === undefined || boundaryCoordinates === undefined
  })
  const {
    data: geoCoordinatesData,
    error: geoCoordinatesError,
    loading: geoCoordinatesLoading
  } = useQuery(GET_GEOCOORDINATES, {
    variables: { csib64, projectedCoordinates: formDatatobeSent },
    skip: formDatatobeSent === undefined
  })

  useEffect(() => {
    if (boundaryInput === BoundaryInputType.wkt) {
      const coordinatesInWKT = convertJSONToWKT(boundaryCoordinates ?? "")
      setFormData(coordinatesInWKT)
      setInitialFormData(coordinatesInWKT)
    } else if (active === CRSType.ProjectedCRS) {
      if (projectedCoordinatesData) {
        const p_coordinates = roundCoordinates(projectedCoordinatesData.getProjectedCoordinates.coordinates)
        setProjectedCoordinates(p_coordinates)
        setFormData(p_coordinates)
        setInitialFormData(p_coordinates)
      } else {
        // if projected coordinates are not available
        // console.log("Error in getting projected coordinates", projectedCoordinatesError?.message)
        setInitialFormData("")
        setFormData("")
      }
    } else {
      setFormData(boundaryCoordinates)
      setInitialFormData(boundaryCoordinates)
    }
  }, [csib64, projectedCoordinatesData, projectedCoordinatesError, boundaryCoordinates, active, boundaryInput])

  useEffect(() => {
    if (geoCoordinatesData) {
      setUpdatedBoundaryCoordinates(reverseCoordinates(geoCoordinatesData.getGeoCoordinates.coordinates))
      setFormDatatoBeSent(undefined)
    }
    if (geoCoordinatesError) {
      // console.log("%cError in getting geo coordinates", "color:red", geoCoordinatesError.message)
      setFormDatatoBeSent(undefined)
      return
    }
  }, [csib64, geoCoordinatesData, geoCoordinatesError, initialFormData])

  useEffect(() => {
    if (updatedBoundaryCoordinates) {
      const updateBoundryInConnect = async () => {
        setLoading(true)
        const api = new ConnectApi(projectOrigin)
        setConnectApi(api)
        const updatedBoundryInConnect = await api.updateBoundaryCoordinates(
          connectProjectId,
          updatedBoundaryCoordinates
        )
        setLoading(false)
        setShowProjectBoundaryModal(false)
        if (updatedBoundryInConnect.address) {
          setBoundaryEdited(true)
        } else {
          setUpdatedBoundaryCoordinates(undefined)
          console.log("Error in updating boundary in connect:", updatedBoundryInConnect.errorCode)
          if (boundaryInput === BoundaryInputType.wkt) {
            const coordinatesInWKT = convertJSONToWKT(boundaryCoordinates ?? "")
            setFormData(coordinatesInWKT)
            setInitialFormData(coordinatesInWKT)
          } else if (active === CRSType.ProjectedCRS) {
            if (ProjectedCoordinates) {
              setFormData(ProjectedCoordinates)
              setInitialFormData(ProjectedCoordinates)
            } else {
              // if projected coordinates are not available
              setInitialFormData("")
              setFormData("")
            }
          } else {
            setFormData(boundaryCoordinates)
            setInitialFormData(boundaryCoordinates)
          }
        }
      }
      updateBoundryInConnect()
    }
  }, [
    projectOrigin,
    updatedBoundaryCoordinates,
    connectProjectId,
    boundaryCoordinates,
    active,
    boundaryInput,
    ProjectedCoordinates
  ])

  useEffect(() => {
    if (boundaryEdited) {
      setBoundaryEdited(false)
      // sync project with quadri
      submitQuadriProject()
    }
  }, [boundaryEdited, submitQuadriProject])

  const closeModal = () => {
    setShowProjectBoundaryModal(false)
  }

  const onSubmit = async () => {
    if (boundaryInput === BoundaryInputType.wkt) {
      if (formData) {
        // dropping holes if any
        const newWKT = dropInteriorRings(formData)
        const updatedCoordinates = convertWktToJSON(newWKT ? newWKT : formData)
        setUpdatedBoundaryCoordinates(updatedCoordinates)
        return
      }
    }

    if (active === CRSType.GeoCRS) {
      setUpdatedBoundaryCoordinates(formData)
      return
    }
    setFormDatatoBeSent(formData)
  }

  return (
    <div className="row">
      <div className="col-12">
        <Card header={t("connectSettings.header")}>
          <div className="col-12 col-lg-4">
            <p className="text-meta">{t("connectSettings.description")}</p>
          </div>
          <div className="col-12 col-lg-8">
            <EditProjectBoundary
              showProjectBoundaryModal={showProjectBoundaryModal}
              setShowProjectBoundaryModal={setShowProjectBoundaryModal}
              csib64={csib64 as string}
              t={t}
              setBoundaryInput={setBoundaryInput}
              disableEdit={disableEdit}
            ></EditProjectBoundary>
            {showProjectBoundaryModal && (
              <EditProjectBoundaryModal
                closeModal={closeModal}
                onSubmit={onSubmit}
                formData={formData}
                setFormData={setFormData}
                initialFormData={initialFormData}
                t={t}
                geoCooedinatesError={geoCoordinatesError}
                geoCoordinatesLoading={geoCoordinatesLoading}
                loading={loading}
                active={active}
                boundaryInput={boundaryInput}
              />
            )}

            <BoundaryItem
              connectProjectId={connectProjectId}
              boundaryCoordinates={boundaryCoordinates}
              mismatch={projectEnabled && !areasMatch(boundaryCoordinates, tcpsArea ? tcpsArea : [])}
              t={t}
              projectedCoordinates={ProjectedCoordinates}
              projectedCoordinatesIsLoading={projectedCoordinatesIsLoading}
              active={active}
              setactive={setActive}
            />
            <CoordinateItem
              connectProjectId={connectProjectId}
              crsName={crsName}
              mismatch={projectEnabled && !crsNamesAreEqual(crsName, tcpsCrsName)}
              t={t}
            />
          </div>
        </Card>
      </div>
    </div>
  )
}

const BoundaryItem = (props: {
  connectProjectId: string
  boundaryCoordinates?: string
  mismatch?: boolean
  t: any
  projectedCoordinates?: string
  projectedCoordinatesIsLoading?: boolean
  geoCoordinatesLoading?: boolean
  active: CRSType
  setactive: React.Dispatch<React.SetStateAction<CRSType>>
}) => {
  const hasButton = true
  return (
    <ProjectSettingGroup
      name={props.t("connectSettings.projectBoundary")}
      projectSettings={[{ name: props.t("connectSettings.coordinates"), value: props.boundaryCoordinates }]}
      optional={true}
      mismatch={props.mismatch}
      message={
        props.mismatch
          ? props.t("connectSettings.mismatch")
          : props.projectedCoordinatesIsLoading
            ? props.t("connectSettings.loading")
            : props.t("connectSettings.boundaryNotDefined")
      }
      hasButton={hasButton}
      projectedCoordinates={props.projectedCoordinates}
      active={props.active}
      setactive={props.setactive}
    />
  )
}
const CoordinateItem = (props: { connectProjectId: string; crsName?: string; mismatch?: boolean; t: any }) => (
  <ProjectSettingGroup
    name={props.t("connectSettings.coordinateReferenceSystem")}
    projectSettings={[{ name: props.t("connectSettings.system"), value: props.crsName }]}
    optional={true}
    mismatch={props.mismatch}
    message={props.mismatch ? props.t("connectSettings.mismatch") : props.t("connectSettings.crsNotDefined")}
  />
)

export default ImportedProjectSettings
